/* eslint-disable i18next/no-literal-string */

import { keyframes } from "@emotion/react";
import {
  Alert,
  Button,
  Icon,
  OsStack,
  Paper,
  Theme,
  Typography,
} from "@outschool/backpack";
import { borderRadius } from "@outschool/backpack/dist-cjs/theme/borderRadius";
import { faChevronLeft, faSparkles } from "@outschool/icons";
import { HeroLayout } from "@outschool/ui-components-classroom";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Loading, LoadingIcon } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import { navigate } from "@patched/hookrouter";
import React, { useState } from "react";

import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import useLoadingButtonText from "../LearnerDiscovery/useLoadingButtonText";
import AnimalSelectionGrid from "./AnimalSelectionGrid";
import AvatarResult from "./AvatarResult";
import { useGenerateAvatarMutation } from "./hooks";

const MAX_SELECTED_ANIMALS = 3;
const MIN_SELECTED_ANIMALS = 2;

const LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

export default function AvatarBuilder() {
  // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
  useAnalyticsPage({ pageName: PageType.AvatarBuilder });
  const currentLearner = useCurrentLearner();
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [generatedAvatar, setGeneratedAvatar] = useState<{
    name: string;
    description: string;
    imageUrl: string;
  } | null>(null);
  const isMobile = useIsMobile();

  const [generateAvatar, { loading: isGenerating, error: generateError }] =
    useGenerateAvatarMutation();
  const handleAnimalToggle = (animalName: string) => {
    setSelectedAnimals(prev => {
      if (prev.includes(animalName)) {
        return prev.filter(name => name !== animalName);
      }
      if (prev.length < MAX_SELECTED_ANIMALS) {
        return [...prev, animalName];
      }
      return prev;
    });
  };

  const loadingButtonText = useLoadingButtonText(isGenerating);

  const handleGenerate = async () => {
    if (selectedAnimals.length < MIN_SELECTED_ANIMALS) {
      return;
    }

    try {
      const result = await generateAvatar({
        variables: { animalNames: selectedAnimals },
      });

      if (result.data?.generateAvatar) {
        setGeneratedAvatar({
          name: result.data.generateAvatar.name,
          description: result.data.generateAvatar.description,
          imageUrl: result.data.generateAvatar.imageUrl,
        });
      }
    } catch (error) {
      // Error is handled by the error state from the mutation
      console.error("Failed to generate avatar:", error);
    }
  };

  if (!currentLearner) {
    return <Loading />;
  }

  // Once avatar is generated, show only the result page (not the builder)
  if (generatedAvatar) {
    return (
      <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
        <AvatarResult
          avatar={generatedAvatar}
          animals={selectedAnimals}
          onBack={() => setGeneratedAvatar(null)}
        />
      </HeroLayout>
    );
  }

  const handleBack = () => {
    // Navigate back to the profile page since that's where users come from
    // when clicking the avatar edit button
    if (currentLearner) {
      navigate(`/profile/${currentLearner.uid}`);
    } else {
      // Fallback to home if no current learner
      navigate("/");
    }
  };

  // Show the builder interface for selecting animals and generating avatar
  return (
    <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
      <OsStack spacing={4}>
        <Button
          startIcon={<Icon icon={faChevronLeft} />}
          size="small"
          sx={{ width: "fit-content" }}
          onClick={handleBack}
        >
          Back
        </Button>
        <Paper
          variant="elevation"
          sx={{
            borderRadius: borderRadius.medium,
            padding: { xs: "16px", md: "32px" },
          }}
        >
          <OsStack
            spacing={{ xs: 4, md: 6 }}
            direction="column"
            alignItems={isMobile ? "center" : "flex-start"}
          >
            <OsStack spacing={{ xs: 2, md: 4 }}>
              <Typography variant="h1">Create Avatar</Typography>
              <Typography variant="body1">
                Create your own awesome profile creature. Mix animals and see
                what new friend appears.
                <br />
                Pick 2 or 3 animals to mix together.
              </Typography>
            </OsStack>
            {/* Animal Selection Grid - Separated */}

            <AnimalSelectionGrid
              selectedAnimals={selectedAnimals}
              onAnimalToggle={handleAnimalToggle}
            />

            {/* Mix Animals Button */}
            {generateError && (
              <Alert
                severity="error"
                title="Your avatar got a little stuck. Please try again"
              />
            )}

            {isGenerating ? (
              <Button
                startIcon={<LoadingIcon />}
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={true}
                sx={(theme: Theme) => ({
                  [theme.breakpoints.up("md")]: {
                    width: "160px",
                  },
                  "&.Mui-disabled": {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    backgroundSize: "400% 100%",
                    animation: `${LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION} 2s ease infinite`,
                    color: theme.palette.common.white,
                  },
                })}
              >
                {loadingButtonText}
              </Button>
            ) : (
              <TrackedButton
                startIcon={<Icon icon={faSparkles} />}
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={selectedAnimals.length < MIN_SELECTED_ANIMALS}
                trackingName="avatar_builder.mix_creature_button_clicked"
                trackingSharedProperties={{
                  animalCount: selectedAnimals.length,
                  animals: selectedAnimals,
                }}
              >
                Mix My Creature!
              </TrackedButton>
            )}
          </OsStack>
        </Paper>
      </OsStack>
    </HeroLayout>
  );
}
